/**
 * Hand-written validator for the SSL level format. Produces readable,
 * path-based error messages ("objects[41].x: expected finite number").
 * Kept dependency-free so the identical module runs in the browser, tests,
 * and (Phase 6) Cloudflare Workers.
 */
import type { LevelData } from './types';
import {
  CURRENT_FORMAT_VERSION,
  PORTAL_KINDS,
  PAD_KINDS,
  ORB_KINDS,
  TRIGGER_KINDS,
  EASINGS,
} from './types';

export class LevelValidationError extends Error {
  constructor(public readonly errors: readonly string[]) {
    super(`Level validation failed:\n${errors.join('\n')}`);
    this.name = 'LevelValidationError';
  }
}

const OBJECT_TYPES = new Set([
  'block',
  'spike',
  'endgate',
  'decoration',
  'portal',
  'pad',
  'orb',
  'slope',
  'oneway',
  'checkpoint',
  'startpos',
  'trigger',
  'collectible',
  'lock',
  'text',
]);
const COLLECTIBLE_KINDS = ['key', 'shard', 'mark'];
const MAX_GROUP_ID = 9999;
const SLOPE_DIRS = new Set(['up', 'down']);
const SLOPE_GRADES = new Set([1, 0.5]);
const KINDS_BY_TYPE: Record<string, readonly string[]> = {
  portal: PORTAL_KINDS,
  pad: PAD_KINDS,
  orb: ORB_KINDS,
  trigger: TRIGGER_KINDS,
};
const START_FORMS = new Set([
  'runner',
  'glider',
  'roller',
  'hopper',
  'streak',
  'vaulter',
  'blinker',
  'pendul',
]);
const SPEEDS = new Set(['half', 'one', 'two', 'three', 'four']);
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

class Ctx {
  errors: string[] = [];
  fail(path: string, msg: string): void {
    this.errors.push(`${path}: ${msg}`);
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function checkFinite(c: Ctx, path: string, v: unknown): v is number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    c.fail(path, `expected finite number, got ${JSON.stringify(v)}`);
    return false;
  }
  return true;
}

function checkString(c: Ctx, path: string, v: unknown, maxLen = 10_000): v is string {
  if (typeof v !== 'string') {
    c.fail(path, `expected string, got ${typeof v}`);
    return false;
  }
  if (v.length > maxLen) {
    c.fail(path, `string too long (${v.length} > ${maxLen})`);
    return false;
  }
  return true;
}

function validateObject(c: Ctx, path: string, v: unknown, seenIds: Set<number>): void {
  if (!isRecord(v)) {
    c.fail(path, 'expected object');
    return;
  }
  if (checkFinite(c, `${path}.id`, v.id)) {
    if (!Number.isInteger(v.id) || (v.id as number) < 0)
      c.fail(`${path}.id`, 'expected non-negative integer');
    else if (seenIds.has(v.id as number)) c.fail(`${path}.id`, `duplicate object id ${v.id}`);
    else seenIds.add(v.id as number);
  }
  if (checkString(c, `${path}.type`, v.type, 64)) {
    const type = v.type as string;
    if (!OBJECT_TYPES.has(type)) {
      c.fail(`${path}.type`, `unknown object type "${type}"`);
    } else {
      const props = isRecord(v.props) ? v.props : undefined;
      const kinds = KINDS_BY_TYPE[type];
      if (kinds) {
        const kind = props?.kind;
        if (typeof kind !== 'string' || !kinds.includes(kind)) {
          c.fail(`${path}.props.kind`, `expected one of ${kinds.join('/')} for type "${type}"`);
        } else if (kind === 'teleport') {
          checkFinite(c, `${path}.props.dx`, props?.dx);
          checkFinite(c, `${path}.props.dy`, props?.dy);
        }
      }
      if (type === 'trigger') {
        const kind = typeof props?.kind === 'string' ? props.kind : '';
        // Group/channel-targeting triggers need a finite target/channel.
        const needsTarget = [
          'move',
          'rotate',
          'scale',
          'alpha',
          'toggle',
          'spawn',
          'stop',
          'pause',
          'resume',
          'followy',
          'keyframe',
          'pickup',
          'item:spawn-if',
        ];
        const needsChannel = ['color', 'pulse'];
        if (needsTarget.includes(kind)) {
          const t = props?.target;
          if (typeof t !== 'number' || !Number.isInteger(t) || t < 1 || t > MAX_GROUP_ID) {
            c.fail(`${path}.props.target`, `expected group id 1..${MAX_GROUP_ID}`);
          }
        }
        if (needsChannel.includes(kind)) {
          const ch = props?.channel;
          if (typeof ch !== 'number' || !Number.isInteger(ch) || ch < 1 || ch > MAX_GROUP_ID) {
            c.fail(`${path}.props.channel`, `expected channel id 1..${MAX_GROUP_ID}`);
          }
        }
        if ((kind === 'color' || kind === 'pulse') && !HEX_COLOR.test(props?.hex as string)) {
          c.fail(`${path}.props.hex`, 'expected #rrggbb hex color');
        }
        if (
          props?.easing !== undefined &&
          !EASINGS.includes(props.easing as (typeof EASINGS)[number])
        ) {
          c.fail(`${path}.props.easing`, `expected one of ${EASINGS.join('/')}`);
        }
        for (const k of [
          'dx',
          'dy',
          'duration',
          'degrees',
          'alpha',
          'delay',
          'zoom',
          'strength',
          'factor',
          'scale',
          'radius',
          'value',
        ] as const) {
          if (props?.[k] !== undefined) checkFinite(c, `${path}.props.${k}`, props[k]);
        }
        if (kind === 'gradient' && !HEX_COLOR.test(props?.hex2 as string)) {
          c.fail(`${path}.props.hex2`, 'expected #rrggbb hex color');
        }
        if (
          kind === 'camera:edge' &&
          !['left', 'right', 'top', 'bottom'].includes(props?.side as string)
        ) {
          c.fail(`${path}.props.side`, 'expected left/right/top/bottom');
        }
        if (kind === 'keyframe') {
          const path_ = props?.path;
          if (
            typeof path_ !== 'string' ||
            !/^(-?[\d.]+,-?[\d.]+,[\d.]+)(;-?[\d.]+,-?[\d.]+,[\d.]+)*$/.test(path_)
          ) {
            c.fail(`${path}.props.path`, 'expected "dx,dy,t;dx,dy,t;…" (blocks and seconds)');
          }
        }
        if (
          props?.maxActivations !== undefined &&
          (!Number.isInteger(props.maxActivations) || (props.maxActivations as number) < 1)
        ) {
          c.fail(`${path}.props.maxActivations`, 'expected integer ≥ 1');
        }
        if (
          kind === 'timewarp' &&
          typeof props?.factor === 'number' &&
          (props.factor < 0.25 || props.factor > 2)
        ) {
          c.fail(`${path}.props.factor`, 'expected 0.25..2');
        }
      }
      if (type === 'collectible') {
        if (!COLLECTIBLE_KINDS.includes(props?.kind as string)) {
          c.fail(`${path}.props.kind`, `expected one of ${COLLECTIBLE_KINDS.join('/')}`);
        }
        if (
          props?.itemId !== undefined &&
          (!Number.isInteger(props.itemId) || (props.itemId as number) < 1)
        ) {
          c.fail(`${path}.props.itemId`, 'expected integer ≥ 1');
        }
      }
      if (type === 'lock') {
        if (
          props?.itemId !== undefined &&
          (!Number.isInteger(props.itemId) || (props.itemId as number) < 1)
        ) {
          c.fail(`${path}.props.itemId`, 'expected integer ≥ 1');
        }
        if (
          props?.needed !== undefined &&
          (!Number.isInteger(props.needed) || (props.needed as number) < 1)
        ) {
          c.fail(`${path}.props.needed`, 'expected integer ≥ 1');
        }
      }
      if (type === 'text') {
        const txt = props?.text;
        if (typeof txt !== 'string' || txt.length === 0 || txt.length > 120) {
          c.fail(`${path}.props.text`, 'expected 1..120 characters');
        }
        if (
          props?.size !== undefined &&
          (typeof props.size !== 'number' || props.size < 0.25 || props.size > 6)
        ) {
          c.fail(`${path}.props.size`, 'expected 0.25..6');
        }
      }
      if (type === 'startpos') {
        if (props?.form !== undefined && !START_FORMS.has(props.form as string)) {
          c.fail(`${path}.props.form`, `expected one of ${[...START_FORMS].join('/')}`);
        }
        if (props?.speed !== undefined && !SPEEDS.has(props.speed as string)) {
          c.fail(`${path}.props.speed`, `expected one of ${[...SPEEDS].join('/')}`);
        }
      }
      if (type === 'slope') {
        if (!SLOPE_DIRS.has(props?.dir as string)) {
          c.fail(`${path}.props.dir`, `expected "up" or "down" for slopes`);
        }
        if (props?.grade !== undefined && !SLOPE_GRADES.has(props.grade as number)) {
          c.fail(`${path}.props.grade`, `expected 1 or 0.5`);
        }
      }
    }
  }
  checkFinite(c, `${path}.x`, v.x);
  checkFinite(c, `${path}.y`, v.y);
  for (const k of ['rot', 'scaleX', 'scaleY', 'z', 'opacity'] as const) {
    if (v[k] !== undefined) checkFinite(c, `${path}.${k}`, v[k]);
  }
  for (const k of ['flipX', 'flipY'] as const) {
    if (v[k] !== undefined && typeof v[k] !== 'boolean') c.fail(`${path}.${k}`, 'expected boolean');
  }
  if (
    v.opacity !== undefined &&
    typeof v.opacity === 'number' &&
    (v.opacity < 0 || v.opacity > 1)
  ) {
    c.fail(`${path}.opacity`, 'expected 0..1');
  }
  if (v.groups !== undefined) {
    if (!Array.isArray(v.groups)) c.fail(`${path}.groups`, 'expected array of group ids');
    else {
      v.groups.forEach((g, gi) => {
        if (!Number.isInteger(g) || (g as number) < 1 || (g as number) > MAX_GROUP_ID) {
          c.fail(`${path}.groups[${gi}]`, `expected integer 1..${MAX_GROUP_ID}`);
        }
      });
    }
  }
  if (
    v.colorChannel !== undefined &&
    (!Number.isInteger(v.colorChannel) ||
      (v.colorChannel as number) < 1 ||
      (v.colorChannel as number) > MAX_GROUP_ID)
  ) {
    c.fail(`${path}.colorChannel`, `expected channel id 1..${MAX_GROUP_ID}`);
  }
  if (v.hsv !== undefined) {
    if (!isRecord(v.hsv)) c.fail(`${path}.hsv`, 'expected {h,s,v} object');
    else {
      checkFinite(c, `${path}.hsv.h`, v.hsv.h);
      checkFinite(c, `${path}.hsv.s`, v.hsv.s);
      checkFinite(c, `${path}.hsv.v`, v.hsv.v);
    }
  }
}

/** Validate raw parsed JSON as LevelData. Throws LevelValidationError with all problems. */
export function validateLevel(raw: unknown): LevelData {
  const c = new Ctx();
  if (!isRecord(raw)) throw new LevelValidationError(['(root): expected object']);

  if (raw.formatVersion !== CURRENT_FORMAT_VERSION) {
    c.fail(
      'formatVersion',
      `expected ${CURRENT_FORMAT_VERSION}, got ${JSON.stringify(raw.formatVersion)} (run migrations first)`,
    );
  }

  if (!isRecord(raw.meta)) c.fail('meta', 'expected object');
  else {
    checkString(c, 'meta.name', raw.meta.name, 100);
    if (typeof raw.meta.name === 'string' && raw.meta.name.trim() === '')
      c.fail('meta.name', 'must not be empty');
    checkString(c, 'meta.author', raw.meta.author, 100);
    if (!Number.isInteger(raw.meta.revision)) c.fail('meta.revision', 'expected integer');
    checkString(c, 'meta.createdAt', raw.meta.createdAt, 64);
    checkString(c, 'meta.updatedAt', raw.meta.updatedAt, 64);
  }

  if (!isRecord(raw.song)) c.fail('song', 'expected object');
  else {
    checkString(c, 'song.id', raw.song.id, 128);
    if (
      checkFinite(c, 'song.bpm', raw.song.bpm) &&
      ((raw.song.bpm as number) < 20 || (raw.song.bpm as number) > 999)
    ) {
      c.fail('song.bpm', 'expected 20..999');
    }
    checkFinite(c, 'song.offsetMs', raw.song.offsetMs);
  }

  if (!isRecord(raw.settings)) c.fail('settings', 'expected object');
  else {
    if (raw.settings.mode !== 'normal' && raw.settings.mode !== 'platformer')
      c.fail('settings.mode', `unsupported mode ${JSON.stringify(raw.settings.mode)}`);
    if (!SPEEDS.has(raw.settings.speed as string))
      c.fail('settings.speed', `expected one of ${[...SPEEDS].join('/')}`);
    if (raw.settings.startForm !== undefined && !START_FORMS.has(raw.settings.startForm as string))
      c.fail('settings.startForm', `expected one of ${[...START_FORMS].join('/')}`);
    for (const k of ['accentColor', 'hazardColor', 'backgroundColor'] as const) {
      if (
        checkString(c, `settings.${k}`, raw.settings[k], 16) &&
        !HEX_COLOR.test(raw.settings[k] as string)
      ) {
        c.fail(`settings.${k}`, 'expected #rrggbb hex color');
      }
    }
  }

  if (raw.colors !== undefined) {
    if (!Array.isArray(raw.colors)) c.fail('colors', 'expected array');
    else {
      const seen = new Set<number>();
      raw.colors.forEach((col, i) => {
        if (!isRecord(col)) {
          c.fail(`colors[${i}]`, 'expected object');
          return;
        }
        if (
          !Number.isInteger(col.id) ||
          (col.id as number) < 1 ||
          (col.id as number) > MAX_GROUP_ID
        ) {
          c.fail(`colors[${i}].id`, `expected integer 1..${MAX_GROUP_ID}`);
        } else if (seen.has(col.id as number)) {
          c.fail(`colors[${i}].id`, `duplicate channel id ${col.id}`);
        } else seen.add(col.id as number);
        if (!HEX_COLOR.test(col.hex as string))
          c.fail(`colors[${i}].hex`, 'expected #rrggbb hex color');
        if (
          col.opacity !== undefined &&
          (typeof col.opacity !== 'number' || col.opacity < 0 || col.opacity > 1)
        ) {
          c.fail(`colors[${i}].opacity`, 'expected 0..1');
        }
      });
    }
  }

  if (!Array.isArray(raw.objects)) c.fail('objects', 'expected array');
  else {
    const seenIds = new Set<number>();
    let endgates = 0;
    raw.objects.forEach((o, i) => {
      validateObject(c, `objects[${i}]`, o, seenIds);
      if (isRecord(o) && o.type === 'endgate') endgates++;
    });
    if (endgates > 1) c.fail('objects', `expected at most one endgate, found ${endgates}`);
  }

  if (c.errors.length > 0) throw new LevelValidationError(c.errors);
  return raw as unknown as LevelData;
}
